import React, { JSX } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Skeleton } from '@mui/material';
import PrivateRoute from './PrivateRoute';
import { useModuleSettings } from '../hooks/moduleSettings';
import { ModuleKey, moduleGateDecision } from '../utils/moduleEnabled';

interface ModuleRouteProps {
    module: ModuleKey;
    children: JSX.Element;
}

/**
 * Schützt eine Route hinter einem optionalen Modul. Fail-closed:
 * - Während des Ladens der Moduleinstellungen wird ein Ladezustand gezeigt
 *   (keine Seite, kein Redirect), damit keine deaktivierten Inhalte kurz erscheinen.
 * - Ist das Modul deaktiviert ODER liegt ein Ladefehler vor, wird auf das
 *   Dashboard umgeleitet. Das Backend bleibt maßgeblich.
 */
const ModuleGate = ({ module, children }: ModuleRouteProps): JSX.Element => {
    const { modules, isLoading, isError } = useModuleSettings();
    const decision = moduleGateDecision(modules, module, { isLoading, isError });

    if (decision === 'loading') {
        return (
            <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 3, md: 5 } }}>
                <Skeleton variant="text" width={180} height={40} sx={{ mb: 3 }} />
                <Skeleton variant="rounded" height={320} />
            </Box>
        );
    }

    if (decision === 'allow') {
        return children;
    }

    return <Navigate to="/dashboard" replace />;
};

const ModuleRoute = ({ module, children }: ModuleRouteProps) => (
    <PrivateRoute>
        <ModuleGate module={module}>{children}</ModuleGate>
    </PrivateRoute>
);

export default ModuleRoute;
